import { lazy, Suspense } from "react"
import {
	createBrowserRouter,
	Navigate,
	Route,
	Routes,
	useLocation,
	useParams,
} from "react-router-dom"
import { Layout } from "@/widgets/app-layout"
import { isInnerSection } from "@/widgets/bot-workspace"
import { BroadcastFinishWatcher } from "../providers/broadcast-finish-watcher"
import { CommandPalette } from "../providers/command-palette"
import { ClientErrorReporter } from "../providers/client-error-reporter"
import { PageSkeleton } from "@/shared/ui/page-skeleton"
import { RouteSwap } from "@/shared/ui/route-swap"

const AdminPage = lazy(() =>
	import("@/pages/admin-posts").then((module) => ({ default: module.AdminPage })),
)
const CreatePostPage = lazy(() =>
	import("@/pages/admin-post-create").then((module) => ({ default: module.CreatePostPage })),
)
const EditPostPage = lazy(() =>
	import("@/pages/admin-post-edit").then((module) => ({ default: module.EditPostPage })),
)
const PostPage = lazy(() =>
	import("@/pages/admin-post").then((module) => ({ default: module.PostPage })),
)
const AnalyticsPage = lazy(() =>
	import("@/pages/analytics").then((module) => ({ default: module.AnalyticsPage })),
)
const TaxonomyPage = lazy(() =>
	import("@/pages/admin-taxonomy").then((module) => ({ default: module.TaxonomyPage })),
)
const AuditLogPage = lazy(() =>
	import("@/pages/audit-log").then((module) => ({ default: module.AuditLogPage })),
)
const McpPage = lazy(() => import("@/pages/mcp").then((module) => ({ default: module.McpPage })))
const ConsentPage = lazy(() =>
	import("@/pages/oauth-consent").then((module) => ({ default: module.ConsentPage })),
)
const BotsPage = lazy(() => import("@/pages/bots").then((module) => ({ default: module.BotsPage })))
// Раздел бота как в Botmaker: меню, воронки, аналитика, рассылки, настройки.
const BotWorkspaceRoutes = lazy(() => import("./bot-workspace-routes"))
// Холст xyflow и dagre нужны только в редакторе: отдельным чанком.
const ScenarioEditorPage = lazy(() =>
	import("@/pages/scenario-editor").then((module) => ({ default: module.ScenarioEditorPage })),
)

/** Публикация переехала на страницу материала: старые ссылки ведут туда. */
function LegacyPublishRedirect() {
	const { id } = useParams<{ id: string }>()
	return <Navigate to={`/admin/posts/${id}`} replace />
}

/**
 * Ключ страницы для перехода. Раздел бота одна страница со своими вкладками: переход между
 * вкладками ведёт он сам, иначе на каждом клике гасли бы и шапка бота, и сами вкладки.
 * Внутренние страницы (рассылка, пользователь, метка) и редактор воронки идут без шапки
 * бота: они сменяют раздел как отдельные страницы.
 */
const pageKey = (pathname: string) => {
	const bot = pathname.match(/^\/admin\/bots\/[^/]+/)?.[0]
	if (!bot) return pathname
	return isInnerSection(pathname.slice(bot.length + 1)) ? pathname : bot
}

function AppRoutes() {
	const location = useLocation()
	return (
		<>
			<ClientErrorReporter />
			<CommandPalette />
			<BroadcastFinishWatcher />
			<Layout>
				<Suspense fallback={<PageSkeleton shell />}>
					<RouteSwap id={pageKey(location.pathname)}>
						<Routes location={location}>
							{/* Адреса те же, что были в мини-аппе, чтобы ссылки внутри экранов остались рабочими. */}
							<Route path="/" element={<Navigate to="/admin" replace />} />
							<Route path="/admin" element={<AdminPage />} />
							<Route path="/admin/create" element={<CreatePostPage />} />
							<Route path="/admin/edit/:id" element={<EditPostPage />} />
							<Route path="/admin/posts/:id" element={<PostPage />} />
							<Route path="/admin/publish/:id" element={<LegacyPublishRedirect />} />
							<Route path="/admin/analytics" element={<AnalyticsPage />} />
							<Route path="/admin/taxonomy" element={<TaxonomyPage />} />
							<Route path="/admin/bots" element={<BotsPage />} />
							<Route path="/admin/audit" element={<AuditLogPage />} />
							<Route path="/admin/mcp" element={<McpPage />} />
							<Route path="/oauth/consent" element={<ConsentPage />} />
							<Route path="/admin/bots/:botId/*" element={<BotWorkspaceRoutes />} />
							<Route
								path="/admin/scenarios/:id"
								element={
									<Suspense fallback={<PageSkeleton className="p-6" />}>
										<ScenarioEditorPage />
									</Suspense>
								}
							/>
							<Route path="*" element={<Navigate to="/admin" replace />} />
						</Routes>
					</RouteSwap>
				</Suspense>
			</Layout>
		</>
	)
}

export const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }])
